import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesignModelDto } from 'src/api/FiretrolApiClient';
import { useDesignModels } from 'src/api/queries/designModels/useDesignModels';
import { useDeleteDesignModel } from 'src/api/queries/designModels/useDesignModelMutations';
import { formatDate } from 'src/utils';
import { useJobs } from 'src/hooks/useJobs';
import { AnchorLink } from 'src/design/molecules/AnchorLink';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { EmptyState } from 'src/design/atoms/EmptyState';
import { Header } from 'src/design/organisms/Header';
import { useTableExports } from 'src/design/organisms/useTableExports';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import {
  DesignModelFilesCell,
  DesignModelStatusCell,
} from 'src/routes/Jobs/DesignModels/components/DesignModelListCells';
import {
  deleteDesignModelCopy,
  getDesignModelListStatus,
} from 'src/routes/Jobs/DesignModels/designModelStatus';

const NEW_MODEL_LABEL = 'New Design Model';
const DATE_FORMAT = 'MMM D, YYYY';

const modelPath = (model: DesignModelDto) =>
  `/jobs/${model.jobId}/design-models/${model.id}`;

const getColumns = (): MRT_ColumnDef<DesignModelDto>[] => [
  {
    accessorKey: 'name',
    header: 'Model Name',
    size: 250,
    Cell: ({ row }) => (
      <AnchorLink to={modelPath(row.original)} size="sm">
        {row.original.name}
      </AnchorLink>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    size: 150,
    accessorFn: (row) => getDesignModelListStatus(row),
    Cell: ({ row }) => <DesignModelStatusCell model={row.original} />,
  },
  {
    id: 'files',
    header: 'Files',
    size: 170,
    enableSorting: false,
    Cell: ({ row }) => <DesignModelFilesCell model={row.original} />,
  },
  {
    accessorKey: 'createdByUser.fullName',
    header: 'Created By',
    size: 150,
  },
  {
    accessorKey: 'createdAt',
    header: 'Created',
    size: 120,
    Cell: ({ cell }) => formatDate(cell.getValue<string>(), DATE_FORMAT),
  },
];

export const DesignModels: React.FC = () => {
  const { selectedJob } = useJobs();
  const navigate = useNavigate();

  const columns = useMemo(() => getColumns(), []);

  const { data, isLoading, isFetching, isError } = useDesignModels(
    selectedJob?.id,
  );
  const designModels = React.useMemo(() => data ?? [], [data]);
  const deleteDesignModel = useDeleteDesignModel(selectedJob?.id ?? -1);
  const createPath = `/jobs/${selectedJob?.id}/design-models/create`;

  const tableState = useTableState({
    identifier: 'design-models',
    columns: columns,
    defaultSorting: [{ id: 'name', desc: false }],
  });

  const tableExports = useTableExports<DesignModelDto>(
    'design-models',
    (data) =>
      data.map((item) => ({
        ['Job Name']: selectedJob?.name,
        ['Job Number']: selectedJob?.jobNumber,
        ['Model Name']: item.name,
        ['Status']: getDesignModelListStatus(item),
        ['Created By']: item.createdByUser?.fullName || '',
        ['Created']: formatDate(item.createdAt, DATE_FORMAT),
      })),
  );

  const createButton = (
    <CreateButton onClick={() => navigate(createPath)}>
      {NEW_MODEL_LABEL}
    </CreateButton>
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 160 } },
      renderRowActions: ({ row }) => {
        const copy = deleteDesignModelCopy(row.original.name);
        return (
          <Group>
            <EditActionIcon
              onClick={() => navigate(modelPath(row.original))}
              label="Edit Model"
            />
            <DeleteActionIcon
              onDelete={async () => {
                await deleteDesignModel.mutateAsync(row.original.id!);
              }}
              label="Delete Model"
              requiredConfirmation={true}
              confirmationTitle={copy.title}
              confirmationText={copy.text}
            />
          </Group>
        );
      },
      renderEmptyRowsFallback: () => (
        <Box p="xl">
          <Stack align="center" gap="sm">
            <EmptyState
              header="No design models yet"
              description="A design model needs an IFC, FAB, and CAD file from AutoSPRINK."
            />
            {createButton}
          </Stack>
        </Box>
      ),
      enableClickToCopy: false,
      columns: columns,
      data: designModels,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => <Group>{createButton}</Group>,
    },
    getRowId: (row) => String(row.id),
    totalCount: designModels.length,
    isLoading,
    isFetching,
    isError,
    ...tableExports,
  });

  if (!selectedJob) return null;

  return (
    <>
      <Header
        title="Design Models"
        helpText="Manage 3D design models for the progress viewer tool"
        breadcrumbProps={{
          items: [
            { text: 'Jobs', href: '/jobs' },
            {
              text: `${selectedJob?.name} - ${selectedJob?.jobNumber}`,
              href: `/jobs/${selectedJob?.id}/summary`,
            },
            { text: 'Design Models' },
          ],
        }}
      />
      {table}
    </>
  );
};
